import { Component, Output, Inject, EventEmitter, OnInit, ViewEncapsulation, AfterViewInit, OnDestroy, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';

import { Utils } from '../../utils/utils';
import { DialogService } from '../../services/dialog.service';
import { SaveService } from '../../services/save.service';
import { DefaultValuesService } from '../../services/defaultvalues.service';
import { GroupFunctionComponent } from '../groupfunction/groupfunction.component';

interface MGMTBranchNode {
    Value: string;
    GroupName: string;
    subGroupsAllowed: boolean;
    Selected?: boolean;
    Disabled?: boolean;
    Indeterminate?: boolean;
    Parent?: MGMTBranchNode;
    Groups: MGMTBranchNode[];
    GroupFunction: string;
    BusinessLevel: string;
    IsNiOPSRelated: boolean;
    IsBranchExcellenceRelated: boolean;
    HierarchyType: string;
    IsHistoric: boolean;
}

@Component({
    selector: 'managementid',
    templateUrl: './managementid.component.html',
    styleUrls: ['./managementid.component.less'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ManagementIdComponent implements OnInit, AfterViewInit, OnDestroy {
    @Output() managementidChanged = new EventEmitter<string>();

    @ViewChild(MatAutocompleteTrigger) mgmtAutocomplete: MatAutocompleteTrigger = {} as MatAutocompleteTrigger;
    @ViewChild(GroupFunctionComponent) GroupFuncComp: GroupFunctionComponent = {} as GroupFunctionComponent;

    protected _onDestroy = new Subject<void>();

    groupfunction: string = 'All';
    selGrpFuncFrmAll = "";
    selectorValue: string = 'All';
    selectedManagementId: string = '';
    allToggelButtonOption = [];
    value = '';
    lastSelectedNode: MGMTBranchNode = {} as MGMTBranchNode;

    userId = '';
    public TREE_DATA: MGMTBranchNode[] = [];
    public arrayFiltered: MGMTBranchNode[] = [];

    public selectedNodes: string[] = [];

    public treeControl: NestedTreeControl<MGMTBranchNode>;
    public dataSource = new MatTreeNestedDataSource<MGMTBranchNode>();

    public searchString = '';
    public showOnlySelected = false;
    Tree_Response_State: number = -2; // 0 -> empty, 1 -> correct and not empty, -1 -> error, -2 -> Calling API

    mode: ManagementMode = ManagementMode.BranchReporting;
    previousSelectedNode: MGMTBranchNode | null = null;
    isCallSucceed: number = 0;
    placeHolder = "";
    previousPlaceHolder = "";

    modeEnum: typeof ManagementMode = ManagementMode;

    constructor(
        private toastrService: ToastrService,
        private route: ActivatedRoute,
        private http: HttpClient,
        @Inject('BASE_URL') private baseUrl: string,
        public defaults: DefaultValuesService,
        private dialog: DialogService,
        private saveService: SaveService,
        private router: Router
    ) {
        this.treeControl = new NestedTreeControl<MGMTBranchNode>((node) => node.Groups);
    }

    ngOnInit() {
        const actualUrl = globalThis.location.pathname;
        this.updateHierarchyTypeBasedOnUrl(actualUrl);
        this.updateModeBasedOnUrl(actualUrl);
    }

    ngAfterViewInit() {
        this.router.events.subscribe((event: any) => {
            if (event instanceof NavigationEnd) {
                this.updateHierarchyTypeBasedOnUrl(event.url);
                this.updateModeBasedOnUrl(event.url);
                this.funcGetUserMgmtBranches();
            }
        });

        this.defaults.languageMessageChanges$.subscribe(() => {
            this.funcGetUserMgmtBranches();
        });

        this.GroupFuncComp.groupFunctionChanged.subscribe((groupFunction: string) => {
            this.groupfunction = groupFunction;
            this.selectFirstMgmtId(groupFunction, true);
        });
    }

    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    private updateHierarchyTypeBasedOnUrl(url: string) {
        const currentComponent = this.defaults.components.find(x => `/${x.Route}` === url);
        if (currentComponent?.HierarchyType) {
            this.defaults.setHierarchyType(currentComponent.HierarchyType);
        }
    }

    // Update mode based on the current URL
    private updateModeBasedOnUrl(url: string) {
        const previousMode = this.mode;

        if (url === '/actualreporting' || url === '/oplkpimanagement' || url === '/file/15' || url === '/file/16' || url === '/file/17' || url === '/market-sheet' || url === '/segment-sheet' || url === '/file/21' || url === '/file/22') {
            this.mode = ManagementMode.Mersy;
        } else if (url === '/branch-excellence-admin' || url === '/assessment' || url === '/focus-plan' || url.includes('/file/32') || url.includes('/file/26') || url.includes('/file/30') || url.includes('/file/31') || url.includes('/file/39')) {
            this.mode = ManagementMode.BranchExcellence;
        } else if (url === '/osh-admin' || url === '/osh-assessment') {
            this.mode = ManagementMode.SSX;
        } else if (url === '/niops' || url === '/file/27') {
            this.mode = ManagementMode.NIOPSReporting;
        } else if (url === '/pe-configuration' || url === '/pequarterreporting' || url === '/peyearlyplanning' || url === '/file/37' || url === '/file/38') {
            this.mode = ManagementMode.PEReporting;
        } else if (url === '/inco-masterdata' || url === '/inco-actualreporting' || url === '/inco-oplreporting') {
            this.mode = ManagementMode.INCO;
        } else {
            this.mode = ManagementMode.BranchReporting;
        }

        if (previousMode !== this.mode) {
            if (this.treeControl.dataNodes && this.treeControl.dataNodes.length > 0) {
                this.previousSelectedNode = this.findCurrentlySelectedNode();
            }
        }
    }

    private changeManagementId() {
        if (this.value !== '-1' && this.value !== this.defaults.managementid) {
            const url = this.baseUrl + Utils.getManagementIdAPI();
            this.defaults.managementid = this.value;
            this.http.post<string>(url, { 'value': this.defaults.managementid }).subscribe(() => {
                this.defaults.notifyManagementIdChange();
                this.placeHolder = this.selectedManagementId;
            }, error => Utils.writeLog(this.http, this.baseUrl, error));
        } else {
            this.placeHolder = this.selectedManagementId;
        }
    }

    // Handle the selection of a management ID
    private onSelect(managementid: any) {
        if (!managementid || !managementid.Value || !this.defaults.managementids) return;
        this.value = managementid.Value;
        if (this.saveService.isSaveButtonShown()) {
            this.dialog.showConfirmDialog("Do you want to save the changes? If you select No, all changes will be lost!", "Save", this);
        } else {
            this.saveService.fireCancelAll();
            this.changeManagementId();
        }
    }

    // Handle the "Yes" action
    private onYes() {
        this.saveService.fireSave();
        this.changeManagementId();
    }

    private onNo() {
        this.saveService.fireCancelAll();
        this.changeManagementId();
    }

    // Check if a node has children
    public hasChild = (_: number, node: MGMTBranchNode) => !!node.Groups && node.Groups.length > 0;

    // Set the parent for a node
    private setParent(node: MGMTBranchNode, Parent: MGMTBranchNode | undefined) {
        if (node) node.Parent = Parent;
        if (node.Groups) {
            node.Groups.forEach((childNode) => {
                this.setParent(childNode, node);
            });
        }
    }

    // Check all parents of a node
    private checkAllParents(node: MGMTBranchNode) {
        if (node.Parent) {
            const descendants = this.treeControl.getDescendants(node.Parent);
            node.Parent.Indeterminate = descendants.some((child) => child.Selected);
            this.checkAllParents(node.Parent);
        }
    }

    // Get the parent string for a node
    private GetParentsString(node: MGMTBranchNode) {
        if (node.Parent) {
            this.treeControl.expand(node.Parent);
            this.selectedManagementId = node.Parent.GroupName + ' / ' + this.selectedManagementId;
            this.GetParentsString(node.Parent);
        }
    }

    public async itemToggle(checked: boolean, node: MGMTBranchNode, persistToBackend: boolean = true) {
        this.updateTreeSelection(node, checked);
        this.value = node.Value;
        this.defaults.managementName = node.GroupName;
        this.selGrpFuncFrmAll = '';
        
        if (!persistToBackend) return;
        
        this.searchString = "";
        const previousGroupFunction = this.defaults.groupFunction;
        
        this.updateGroupFunction(node.GroupFunction);
        
        if (previousGroupFunction !== node.GroupFunction) {
            await this.GroupFuncComp.sendPost();
        }
        
        this.onSelect(node);
        this.closeAutocomplete();
    }

    private updateGroupFunction(groupFunction: string) {
        if (!groupFunction) return;
        
        this.defaults.groupFunction = groupFunction.split('/')[0];
    }

    private updateTreeSelection(node: MGMTBranchNode, checked: boolean) {
        this.selectedManagementId = "";
        this.unselectAll();
        this.treeControl.collapseAll();
        node.Selected = checked;
        this.selectedManagementId = node.GroupName;
        this.checkAllParents(node);
        this.GetParentsString(node);
    }

    public async selectFirstMgmtId(groupFunctionSelected: string, isFilterChange = false) {
        this.groupfunction = groupFunctionSelected;
        this.arrayFiltered = this.filterNodesByGroupFunction(this.groupfunction);

        const matchNode = this.findMatchNode();

        if (matchNode) {
            await this.handleMatchNode(matchNode, isFilterChange);
        } else {
            this.handleNoMatchNode(isFilterChange);
        }
    }

    private findMatchNode(): MGMTBranchNode | undefined {
        return this.arrayFiltered.find(node => node?.Value === this.defaults.managementid);
    }

    private async handleMatchNode(matchNode: MGMTBranchNode, isFilterChange: boolean = false) {
        if (!this.isNodeValidForCurrentMode(matchNode) || matchNode.IsHistoric) {
            this.handleNoMatchNode(false);
            return;
        }

        await this.itemToggle(true, matchNode, !isFilterChange);
    }

    private handleNoMatchNode(isFilterChange: boolean) {
        this.unselectAll();

        if (isFilterChange) {
            return;
        }

        const relatedNode = this.findRelatedNodeForModeTransition();
        if (relatedNode) {
            const nodeInCurrentTree = this.searchNode(relatedNode.Value, this.treeControl.dataNodes) || relatedNode;
            this.selectNodeAndPersist(nodeInCurrentTree);
            return;
        }

        const firstValidNode = this.findFirstValidNodeForMode();
        if (firstValidNode) {
            this.selectNodeAndPersist(firstValidNode);
        }
    }

    private findFirstValidNodeForMode(): MGMTBranchNode | null {
        return this.findInTree(this.treeControl.dataNodes, (node) => {
            const isValid = this.isNodeValidForCurrentMode(node);
            const passesFilter = this.filterNode(node);
            return isValid && passesFilter && !node.IsHistoric;
        });
    }

    private async selectNodeAndPersist(node: MGMTBranchNode) {
        this.updateTreeSelection(node, true);
        this.value = node.Value;
        this.defaults.managementName = node.GroupName;
        
        const previousGroupFunction = this.defaults.groupFunction;
        this.updateGroupFunction(node.GroupFunction);
        
        if (previousGroupFunction !== node.GroupFunction) {
            await this.GroupFuncComp.sendPost();
        }
        
        this.changeManagementId();
    }

    private unselectAll() {
        this.treeControl.dataNodes?.forEach((branchNode) => {
            branchNode.Selected = false;
            branchNode.Indeterminate = false;
            this.treeControl.getDescendants(branchNode)?.forEach((node) => {
                node.Selected = false;
                node.Indeterminate = false;
            });
        });
    }

    // Hide leaf nodes based on certain conditions
    public hideLeafNode(node: MGMTBranchNode): boolean {
        return (this.searchString !== '' && !new RegExp(this.searchString, 'i').test(node.GroupName))
            || this.shouldHideNodeByMode(node);
    }

    // Hide leaf nodes based on group function selector
    public hideLeafNodeselector(selVal, node: MGMTBranchNode): boolean {
        return !this.groupFuncExist(selVal, node)
            || this.shouldHideNodeByMode(node);
    }

    // Check if a group function exists for a node
    public groupFuncExist(selVal: string, node: MGMTBranchNode) {
        if (!node.GroupFunction || !selVal) return false;
        
        const selValLower = selVal.toLowerCase();
        if (selValLower === 'all') return true;
        
        const groupFunction = node.GroupFunction;
        if (groupFunction.includes('/')) {
            const namesArray = groupFunction.split('/').map(name => name.trim().toLowerCase());
            return namesArray.includes(selValLower);
        }
        return groupFunction.trim().toLowerCase() === selValLower;
    }

    // Hide parent nodes based on certain conditions
    public hideParentNode(node: MGMTBranchNode): boolean {
        const searchLower = this.searchString.toLowerCase();
        
        if (this.searchString !== '' && node.GroupName.toLowerCase().includes(searchLower)) {
            return false;
        }
        
        const descendants = this.treeControl.getDescendants(node);
        return !descendants.some((descendantNode) => 
            descendantNode.GroupName.toLowerCase().includes(searchLower)
        );
    }

    // Hide parent nodes based on group function selector
    public hideParentNodeselector(selVal: string, node: MGMTBranchNode): boolean {
        if (!selVal) return false;
        
        const selValLower = selVal.toLowerCase();
        
        if (node.GroupFunction?.toLowerCase().includes(selValLower)) {
            return false;
        }
        
        const descendants = this.treeControl.getDescendants(node);
        return !descendants.some((descendantNode) => 
            descendantNode.GroupFunction && this.groupFuncExist(selVal, descendantNode)
        );
    }

    // Hide parent management nodes based on certain conditions
    public hideParentManagement(node: MGMTBranchNode): boolean {
        // If the node is valid on its own, never hide it.
        if (this.isNodeValidForCurrentMode(node)) {
            return false;
        }
        const descendants = this.treeControl.getDescendants(node);
        return !descendants.some((descendantNode) => this.descendantMatchesModeCriteria(node, descendantNode));
    }

    private descendantMatchesModeCriteria(parentNode: MGMTBranchNode, descendantNode: MGMTBranchNode): boolean {
        switch (this.mode) {
            case ManagementMode.Mersy:
                return true;
            case ManagementMode.NIOPSReporting:
                return descendantNode.IsNiOPSRelated || parentNode.IsNiOPSRelated;
            case ManagementMode.PEReporting:
                return this.isTaxRelated(descendantNode) === true || this.isTaxRelated(parentNode) === true;
            case ManagementMode.BranchReporting:
                return descendantNode.BusinessLevel === 'FIELDBRANCH';
            case ManagementMode.BranchExcellence:
                if (descendantNode.BusinessLevel === 'FIELDBRANCH') {
                    return true;
                }
                return descendantNode.BusinessLevel === 'MANAGEMENT' && descendantNode.IsBranchExcellenceRelated;
            case ManagementMode.SSX:
                return descendantNode.BusinessLevel === 'MANAGEMENT' || descendantNode.BusinessLevel === 'FIELDBRANCH';
            case ManagementMode.INCO:
                return true;
            default:
                return false;
        }
    }

    private shouldHideNodeByMode(node: MGMTBranchNode): boolean {
        return !this.isNodeValidForCurrentMode(node);
    }

    // Fetch user management branches
    private funcGetUserMgmtBranches() {
        this.Tree_Response_State = 1;
        var temp: any;
        temp = this.defaults.managementids;

        this.TREE_DATA = temp;
        this.treeControl.dataNodes = this.TREE_DATA;

        // Filter nodes based on the defaults.hierarchyType
        this.filterNodesByHierarchyType();

        //this.dataSource.data = this.TREE_DATA;
        Object.keys(this.dataSource.data).forEach((key) => {
            this.setParent(this.dataSource.data[key], undefined);
        });
        
        if (this.defaults.groupFunction?.includes('/')) {
            this.defaults.groupFunction = this.defaults.groupFunction.split('/')[0];
        }
        
        this.selectFirstMgmtId(this.GroupFuncComp.selectedgroupFunction);
        this.placeHolder = this.selectedManagementId;
    }

    // Expand all nodes in the tree control
    treeControlFunc() {
        this.searchString === "" ? "" : this.treeControl.expandAll();
    }

    // Close the autocomplete panel
    closeAutocomplete() {
        if (this.mgmtAutocomplete) {
            this.mgmtAutocomplete.closePanel();
        }
    }

    // Handle the autocomplete closed event
    onAutocompleteClosed() {
        //this.GroupFuncComp.resetGrpFunctionDefault();
        this.GroupFuncComp.setGrpFuncSelctToAll();
        this.searchString = "";
        this.placeHolder = this.previousPlaceHolder;
        this.selectFirstMgmtId(this.GroupFuncComp.selectedgroupFunction, true);
    }

    // Handle the autocomplete opened event
    onAutocompleteOpened() {
        this.previousPlaceHolder = this.placeHolder;
        this.placeHolder = "Search";
    }

    // Get the parent node by child node
    getParentByChild(object: MGMTBranchNode, branchId: string): MGMTBranchNode | undefined {
        if (object.Value === branchId) {
            return object;
        }
        if (object?.Groups) {
            for (const item of object?.Groups) {
                const result = this.getParentByChild(item, branchId);
                if (result !== null && result?.Parent) {
                    return result;
                }
            }
        }
        return undefined;
    }

    // Show the expand icon for a node
    showExpandIcon(node: MGMTBranchNode) {
        return this.mode === ManagementMode.BranchReporting
            || this.mode === ManagementMode.BranchExcellence
            || this.mode === ManagementMode.SSX
            || this.mode === ManagementMode.PEReporting && node.Groups.some(child => this.searchRelatedDescendants(child, ManagementMode.PEReporting))
            || this.mode === ManagementMode.Mersy && !node.Selected && this.hasManagementDescendant(node)
            || this.mode === ManagementMode.INCO && !node.Selected && this.hasManagementDescendant(node)
            || this.mode === ManagementMode.NIOPSReporting && node.Groups.some(child => this.searchRelatedDescendants(child));

    }

    private hasManagementDescendant(node: MGMTBranchNode): boolean {
        if (!node.Groups?.length) return false;
        return node.Groups.some(child =>
            child.BusinessLevel === 'MANAGEMENT' || this.hasManagementDescendant(child)
        );
    }

    // Filter nodes based on certain conditions
    filterNode(node: MGMTBranchNode) {
        if (this.mode === ManagementMode.BranchReporting || 
            this.mode === ManagementMode.SSX || 
            this.mode === ManagementMode.BranchExcellence || 
            this.mode === ManagementMode.NIOPSReporting || 
            this.mode === ManagementMode.PEReporting) {
            return this.isNodeValidForCurrentMode(node);
        }

        return (this.mode === ManagementMode.Mersy && node.BusinessLevel !== 'FIELDBRANCH' && this.isTaxRelated(node) !== true)
            || (this.mode === ManagementMode.INCO && node.BusinessLevel !== 'FIELDBRANCH');
    }

    async updateGrpFunfrmDrpDwn(node: MGMTBranchNode, grpFunc: string) {
        this.updateTreeSelection(node, true);
        this.value = node.Value;
        this.defaults.managementName = node.GroupName;
        this.selGrpFuncFrmAll = '';
        this.searchString = "";
        
        this.defaults.groupFunction = grpFunc;
        this.GroupFuncComp.selectedgroupFunction = grpFunc;
        this.groupfunction = grpFunc;
        await this.GroupFuncComp.sendPost();
        this.defaults.notifyManagementIdChange();
        
        this.onSelect(node);

        this.closeAutocomplete();
    }

    // Filter nodes by group function
    public filterNodesByGroupFunction(groupFunction: string) {
        this.arrayFiltered = [];
        const isAllFilter = groupFunction.toLowerCase() === 'all';
        
        this.treeControl.dataNodes.forEach((branchNode) => {
            if (isAllFilter || (branchNode.GroupFunction && this.groupFuncExist(groupFunction, branchNode))) {
                if (this.filterNode(branchNode)) this.arrayFiltered.push(branchNode);
            }
            if (branchNode.Groups?.length > 0) {
                const alldecendents = this.treeControl.getDescendants(branchNode);
                alldecendents.forEach((node) => {
                    if (isAllFilter || (node.GroupFunction && this.groupFuncExist(groupFunction, node))) {
                        if (this.filterNode(node)) this.arrayFiltered.push(node);
                    }
                });
            }
        });
        return this.arrayFiltered;
    }

    // Search for a node by value
    private searchNode(value: string, nodes: MGMTBranchNode[]): MGMTBranchNode | null {
        return this.findInTree(nodes, (node) => node.Value === value);
    }
    isTaxRelated(node: MGMTBranchNode) {
        if (node.GroupFunction) {
            return node.GroupFunction.toLowerCase() == 'tax' || node.BusinessLevel.toLowerCase() == 'pe'
        }
        else
            return false
    }
    // Recursive method to search for IsNiOPSRelated property
    private searchRelatedDescendants(node: MGMTBranchNode, MgmtMode: ManagementMode = ManagementMode.NIOPSReporting): boolean {
        
        if (node.Groups && node.Groups.length > 0) {
            for (let childNode of node.Groups) {
                if (ManagementMode.PEReporting) {
                    if (this.isTaxRelated(childNode)) return true
                }
                if (this.searchRelatedDescendants(childNode)) {
                    return true;
                }
            }
        }       
        switch (MgmtMode) {
            case ManagementMode.NIOPSReporting:
                return node.IsNiOPSRelated;
            case ManagementMode.PEReporting:
                return this.isTaxRelated(node);
            default:
                return false;
        }
    }

    // Filter nodes by Hieararchy Type
    private filterNodesByHierarchyType() {
        const menuType = this.defaults.hierarchyType || 'ALL';

        const hierarchyFiltered = this.filterHierarchy(this.treeControl.dataNodes,
            (node) => !!node.HierarchyType
                && (menuType === 'ALL' || node.HierarchyType === 'ALL' || node.HierarchyType === menuType));

        this.treeControl.dataNodes = hierarchyFiltered;
        this.dataSource.data = hierarchyFiltered;
    }

    private filterHierarchy(nodes: MGMTBranchNode[], condition: (node: MGMTBranchNode) => boolean): MGMTBranchNode[] {
        return nodes
            .map(node => ({ ...node, Groups: this.filterHierarchy(node.Groups ?? [], condition) }))
            .filter(node => condition(node) || node.Groups.length > 0);
    }

    
    private findInTree(nodes: MGMTBranchNode[], predicate: (node: MGMTBranchNode) => boolean): MGMTBranchNode | null {
        for (const node of nodes) {
            if (predicate(node)) return node;
            if (node.Groups?.length > 0) {
                const found = this.findInTree(node.Groups, predicate);
                if (found) return found;
            }
        }
        return null;
    }

    private findCurrentlySelectedNode(): MGMTBranchNode | null {
        return this.findInTree(this.treeControl.dataNodes, (node) => !!node.Selected);
    }

    private findRelatedNodeForModeTransition(): MGMTBranchNode | null {
        if (!this.previousSelectedNode) return null;
        
        if (this.isNodeValidForCurrentMode(this.previousSelectedNode)) {
            return null;
        }
        
        const previousBusinessLevel = this.previousSelectedNode.BusinessLevel;
        
        if (previousBusinessLevel === 'MANAGEMENT') {
            return this.findFirstValidChildDirect(this.previousSelectedNode);
        }
        
        if (previousBusinessLevel === 'FIELDBRANCH') {
            return this.findFirstValidParentDirect(this.previousSelectedNode);
        }
        
        return null;
    }
   
    private findFirstValidChildDirect(node: MGMTBranchNode): MGMTBranchNode | null {
        if (!node.Groups?.length) return null;
        
        for (const child of node.Groups) {
            if (this.isNodeValidForCurrentMode(child)) {
                return child;
            }
            
            // If child is not valid BUT is intermediate node (Value=null), 
            // continue searching its children
            if (child.Value === null && child.Groups?.length) {
                const validGrandchild = this.findFirstValidChildDirect(child);
                if (validGrandchild) {
                    return validGrandchild;
                }
            }
        }
        
        return null;
    }
  
    private findFirstValidParentDirect(node: MGMTBranchNode): MGMTBranchNode | null {
        if (!node.Parent) return null;
        
        if (this.isNodeValidForCurrentMode(node.Parent)) {
            return node.Parent;
        }
        
        // If parent is not valid (can be "All Branches" with Value=null), 
        // continue moving up
        if (node.Parent.Value === null) {
            return this.findFirstValidParentDirect(node.Parent);
        }
        
        return null;
    }
  
    private isNodeValidForCurrentMode(node: MGMTBranchNode): boolean {
        switch (this.mode) {
            case ManagementMode.Mersy:
                return node.BusinessLevel === 'MANAGEMENT';
            case ManagementMode.NIOPSReporting:
                return node.IsNiOPSRelated;
            case ManagementMode.PEReporting:
                return this.isTaxRelated(node);
            case ManagementMode.BranchReporting:
                return node.BusinessLevel === 'FIELDBRANCH' || (node.BusinessLevel === 'MANAGEMENT' && node.Groups?.length >= 1);
            case ManagementMode.SSX:
                return node.BusinessLevel === 'FIELDBRANCH' || node.BusinessLevel === 'MANAGEMENT';
            case ManagementMode.BranchExcellence:
                return node.BusinessLevel === 'FIELDBRANCH' || 
                       (node.BusinessLevel === 'MANAGEMENT' && node.IsBranchExcellenceRelated);
            case ManagementMode.INCO:
                return node.BusinessLevel === 'MANAGEMENT';
            default:
                return true;
        }
    }

    public isParentCheckboxDisabled(node: MGMTBranchNode): boolean {
        return !node.GroupFunction
            || node.Selected
            || (this.mode === ManagementMode.BranchExcellence && node.IsBranchExcellenceRelated === false)
            || (this.mode === ManagementMode.NIOPSReporting && node.IsNiOPSRelated === false)
            || (this.mode === ManagementMode.PEReporting && this.isTaxRelated(node) === false);
    }

    public isNodeSelectable(node: MGMTBranchNode): boolean {
        if (!node.GroupFunction || node.Disabled) return false;
        return this.isNodeValidForCurrentMode(node);
    }
}

export enum ManagementMode {
    Mersy = 0,
    BranchExcellence = 1,
    BranchReporting = 2,
    NIOPSReporting = 3,
    PEReporting = 4,
    SSX = 5,
    INCO = 6
}